import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../auth/hooks/useAuth'
import { routeForNotification } from '../lib/notificationRoutes'
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
} from '../../../shared/store/slices/notificationsSlice'
import type { AppDispatch, RootState } from '../../../shared/store'
import { Button } from '../../../shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../shared/components/ui/dropdown-menu'
import { cn } from '../../../shared/lib/utils'
import { BellIcon, CheckIcon } from 'lucide-react'

export function NotificationBell() {
  const dispatch = useDispatch<AppDispatch>()
  const { role } = useAuth()
  const { items, unreadCount } = useSelector((state: RootState) => state.notifications)

  useEffect(() => {
    void dispatch(fetchNotifications())
  }, [dispatch])

  const handleMarkAsRead = (id: string) => {
    void dispatch(markNotificationAsRead(id))
  }

  const handleMarkAllAsRead = () => {
    void dispatch(markAllNotificationsAsRead())
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            unreadCount > 0
              ? `Notifications, ${unreadCount > 9 ? '9+' : unreadCount} unread`
              : 'Notifications'
          }
        >
          <BellIcon className="h-5 w-5" />
          {unreadCount > 0 && (
            <span
              aria-hidden="true"
              className="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-xs font-normal text-muted-foreground hover:text-foreground transition-colors"
            >
              Mark all as read
            </button>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {items.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">No notifications</div>
        ) : (
          <div className="max-h-80 overflow-y-auto">
            {items.map((notification) => {
              const body = (
                <>
                  <div className="flex w-full items-start justify-between gap-2">
                    <span className="text-sm font-medium leading-tight">{notification.title}</span>
                    {notification.is_read && (
                      <CheckIcon className="h-3 w-3 text-muted-foreground flex-shrink-0 mt-0.5" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground leading-snug">
                    {notification.message}
                  </span>
                  {notification.created_at && (
                    <span className="text-xs text-muted-foreground/70">
                      {new Date(notification.created_at).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  )}
                </>
              )
              // Deep link where the target is known (008 R10); otherwise a plain row
              const href = routeForNotification(notification, role)
              const rowClass = cn(
                'flex flex-col items-start gap-1 px-3 py-3 cursor-pointer',
                !notification.is_read && 'bg-accent/50'
              )
              const onSelect = () => !notification.is_read && handleMarkAsRead(notification.id)
              return href ? (
                <DropdownMenuItem key={notification.id} asChild className={rowClass}>
                  <Link to={href} onClick={onSelect}>
                    {body}
                  </Link>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem key={notification.id} className={rowClass} onClick={onSelect}>
                  {body}
                </DropdownMenuItem>
              )
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
